import ControllerBase from '../../../../global/js/ControllerBase';
import { BehaviorSubject, fromEvent, Observable, of, Subject } from 'rxjs';
import { HttpActionResponse } from '../../../../global/interfaces/http.interface';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { ProductListResponseModel } from './o-product-list.model';
import { fromFetch } from 'rxjs/fetch';
import { MoleculeProductProps } from 'wolf/components/molecules/m-teaser-product/m-teaser-product.data';
import ControllerProxy from '../../../../global/js/ControllerProxy';
import Controller from '../../../global/js/controller';
import { ProductListPaginationOptions } from './o-product-list.mock';

const productTeaserTemplate = require('components/molecules/m-teaser-product/m-teaser-product.template.twig') as any;

export default class ControllerProductList extends ControllerBase {
	private readonly componentName = 'o-product-list';

	// /** A list of dom element references available to this component */
	private readonly componentRefs = {
		loadMoreButton: 'loadMoreButton',
		gridContainer: 'gridContainer',
	};

	private readonly productListUrlBase = 'https://fetch.mock/o-product-list'; // see mock file for endpoints

	/** handle subscriptions/memory */
	private _unsubscribeAll: Subject<any>;

	private paginationSettings: BehaviorSubject<ProductListPaginationOptions>;
	private readonly defaultPageSize = 4;

	constructor(element: HTMLElement) {
		super(element);

		// watch for product line changes and call API
		this.observeLoadMoreClick();

		// handle component subscriptions/memory
		this._unsubscribeAll = new Subject();
		window.addEventListener('unload', (e) => {
			this._unsubscribeAll.next();
			this._unsubscribeAll.complete();
		});

		this.domUtils.onDomElementChanged.subscribe((ref) => {
			if (ref) {
				// console.info("DOM changed for " + ref, element);
				this.initializeNestedComponents();
			}
		});

		// @todo - this should be initialized based on the URL or external data
		this.paginationSettings = new BehaviorSubject({
			pageNumber: 1,
			pageSize: this.defaultPageSize,
		});
	}

	/**
	 * Watch for clicks on the load more button and call the API
	 * @returns void
	 */
	private observeLoadMoreClick(): void {
		const loadMoreWrapper = this.$references[this.componentRefs.loadMoreButton];
		const buttons = loadMoreWrapper && loadMoreWrapper.querySelectorAll(':scope a');
		const button = buttons && buttons.length > 0 ? (buttons[0] as HTMLAnchorElement) : undefined;

		button &&
			fromEvent(button, 'click')
				.pipe(
					tap((e) => e.preventDefault()),
					map((e: Event) => (e && e.target) as HTMLAnchorElement),
				)
				.subscribe((et: HTMLAnchorElement) => {
					if (et) {
						this.loadMore(et);
					}
				});
	}

	/**
	 * Call the products list API and add more products to the page
	 * @param  {HTMLAnchorElement} $event? The target load more button
	 * @returns void
	 */
	private loadMore($event?: HTMLAnchorElement): void {
		this.fetchProducts()
			.pipe(
				takeUntil(this._unsubscribeAll),
				catchError((error) => {
					// console.log('error: ', error);
					return of(error);
				}),
				map((_data: HttpActionResponse<ProductListResponseModel, any>) => {
					return _data && _data.data;
				}),
			)
			.subscribe((resp) => {
				if (resp && resp.productList) {
					this.renderMoreProducts(resp.productList);
				}
			});
	}

	/**
	 * Returns an observable of the API response
	 * @returns Observable
	 */
	private fetchProducts(): Observable<ProductListResponseModel> {
		const url = new URL(this.productListUrlBase + '/getProducts');

		// setup pagination to point to the next page
		this.nextPage();

		// append pagination settings to the URL string
		for (const key in this.paginationSettings.getValue()) {
			if (Object.prototype.hasOwnProperty.call(this.paginationSettings.getValue(), key)) {
				const element = this.paginationSettings.getValue()[key];
				url.searchParams.append(key, element);
			}
		}

		return fromFetch<ProductListResponseModel>(url.toString(), {
			selector: (response) => response.json(),
		}).pipe();
	}

	/**
	 * Renders more products onto the page
	 * @param  {MoleculeProductProps[]} products An array of products
	 * @returns void
	 */
	private renderMoreProducts(products: MoleculeProductProps[]): void {
		const gridDiv = this.$references[this.componentRefs.gridContainer];
		if (gridDiv) {
			const productsHtml: string[] = products.map((p) => {
				return productTeaserTemplate({ data: p });
			});
			productsHtml &&
				productsHtml.forEach((p) => {
					const dom = new DOMParser();
					const newProductContainer = document.createElement<'div'>('div');
					// add a fadein class for smooth fadein animation
					newProductContainer.classList.add('fadein');

					newProductContainer.appendChild(dom.parseFromString(p, 'text/html').body.children[0]);
					gridDiv.appendChild(newProductContainer);
				});

			this.domUtils.domElementChanged(this.componentRefs.gridContainer);
		}
	}

	/**
	 * Re-init the controllers on DOM changes
	 * @todo - this is copied from shopping cart controller - make common function?
	 * @returns void
	 */
	private initializeNestedComponents(): void {
		const components = this.$element.querySelectorAll('[data-controller]');
		components.forEach((element: HTMLElement) => {
			const componentName = <string>element.dataset.controller;
			new ControllerProxy(Controller, componentName, element);
		});
	}

	/**
	 * Set the pagination to be the next or previous page
	 * @param  {1|-1=1} advance The advance mode, forward or backwards
	 * @returns void
	 */
	private nextPage(advance: 1 | -1 = 1): void {
		const current = this.paginationSettings.getValue();
		this.paginationSettings.next({
			pageNumber: (current.pageNumber ?? advance) + 1,
			pageSize: current.pageSize,
		});
	}
}
